'use client';

import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import type { ReactNode } from 'react';
import { GRUND, RörelseProvider, TYPSNITT, hexTillRgba, useRörelse } from 'src/components/rörelse';

/**
 * Skalet runt varje presentation.
 *
 * Medvetet inte SimpleLayout: den drar in applogga, hjälplänk och inställningsknapp,
 * alltså produktskal. Här ska ingenting konkurrera med scenerna.
 */
export function PresentationRam({ children }: { children: ReactNode }) {
    return (
        <RörelseProvider>
            <Box
                data-presentation=""
                sx={{
                    minHeight: '100dvh',
                    bgcolor: GRUND.botten,
                    color: GRUND.text,
                    fontFamily: TYPSNITT.bröd,
                    position: 'relative',
                    overflowX: 'hidden'
                }}
            >
                <Box component="a" href="#innehall" className="hoppa-till">
                    Hoppa till innehållet
                </Box>
                <RörelseReglage />
                <Box component="main" id="innehall" sx={{ position: 'relative', zIndex: 1 }}>
                    {children}
                </Box>
            </Box>
        </RörelseProvider>
    );
}

function RörelseReglage() {
    const { mindreRörelse, systemetBegär, sättManuellt } = useRörelse();

    return (
        <Box
            sx={{
                // Uppe till höger, inte nere: rubrikerna sitter till vänster och ytan
                // uppe till höger är tom i varje scen. Nere till höger låg reglaget över
                // beloppen i bankens kontoliggare på mobil.
                position: 'fixed',
                right: { xs: 12, md: 20 },
                top: { xs: 12, md: 20 },
                zIndex: 20
            }}
        >
            <ButtonBase
                onClick={() => sättManuellt(!mindreRörelse)}
                disabled={systemetBegär}
                aria-pressed={mindreRörelse}
                title={
                    systemetBegär
                        ? 'Ditt system är inställt på minskad rörelse, så den är alltid på här.'
                        : undefined
                }
                sx={{
                    px: { xs: 1.4, md: 1.75 },
                    py: { xs: 0.8, md: 1 },
                    borderRadius: 999,
                    fontSize: { xs: 12, md: 13 },
                    fontFamily: TYPSNITT.bröd,
                    color: mindreRörelse ? GRUND.botten : GRUND.dämpad,
                    bgcolor: mindreRörelse ? GRUND.text : hexTillRgba('#12151F', 0.86),
                    border: `1px solid ${hexTillRgba('#F2F3F7', mindreRörelse ? 0 : 0.18)}`,
                    backdropFilter: 'blur(10px)',
                    transition: 'color .18s, background-color .18s',
                    '&:hover': { color: mindreRörelse ? GRUND.botten : GRUND.text },
                    '&.Mui-disabled': { opacity: 0.85, color: GRUND.botten, bgcolor: GRUND.text }
                }}
            >
                Mindre rörelse{systemetBegär ? ' (systemets val)' : ''}
            </ButtonBase>
        </Box>
    );
}
